import { logger } from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Chip
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Payments as PaymentsIcon
} from '@mui/icons-material';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ErrorDisplay } from '../../components/common/ErrorDisplay';

// Add this helper function to safely access nested properties
const safeGet = (obj: any, path: string, defaultValue: any = 'N/A') => {
  try {
    const keys = path.split('.');
    let result = obj;
    for (const key of keys) {
      if (result === undefined || result === null) return defaultValue;
      result = result[key];
    }
    return result === undefined || result === null ? defaultValue : result;
  } catch (e) {
    logger.error(`Error accessing path ${path}:`, e);
    return defaultValue;
  }
};

const TaxCalculationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [calculation, setCalculation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  
  useEffect(() => {
    fetchCalculationDetails();
  }, [id]);
  
  const fetchCalculationDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`/api/v1/tax/calculations/${id}`);
      setCalculation(response.data);
    } catch (err) {
      logger.error('Error fetching calculation details:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper to format dates
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Helper to format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };
  
  // Go back to calculator
  const goBack = () => {
    navigate('/tax/calculator');
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={goBack}
          sx={{ mb: 2 }}
        >
          Back to Calculator
        </Button>
        <ErrorDisplay 
          error={error} 
          userMessage="Failed to load calculation details." 
        />
      </Box>
    );
  }
  
  if (!calculation) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={goBack}
          sx={{ mb: 2 }}
        >
          Back to Calculator
        </Button>
        <Typography variant="h6">Calculation not found</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={goBack}
        sx={{ mb: 2 }}
      >
        Back to Calculator
      </Button>
      
      <Paper 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        elevation={3} 
        sx={{ p: 3, mb: 3 }}
      >
        <Typography variant="h4" gutterBottom>
          Tax Calculation Details
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CalendarIcon color="action" sx={{ mr: 1 }} />
          <Typography variant="body1">
            {formatDate(calculation.created_at)}
          </Typography>
        </Box>
        
        {calculation.calculation_ref && (
          <Box sx={{ mb: 2 }}>
            <Chip 
              label={`Calculation ID: ${calculation.calculation_ref}`} 
              variant="outlined" 
              size="small"
            />
          </Box>
        )}
        
        <Grid container spacing={3}>
          {/* Summary */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>
              
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Typography variant="subtitle2">Subtotal</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography>
                          {formatCurrency(calculation.amount_total - calculation.tax_amount_exclusive, calculation.currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell>
                        <Typography variant="subtitle2">Tax</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography>
                          {formatCurrency(calculation.tax_amount_exclusive, calculation.currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell>
                        <Typography variant="subtitle1" fontWeight="bold">Total</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle1" fontWeight="bold">
                          {formatCurrency(calculation.amount_total, calculation.currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <LocationIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Location
                </Typography>
                <Typography variant="body2">
                  {calculation.customer_details?.address?.line1 && (
                    <>
                      {calculation.customer_details.address.line1}<br />
                      {calculation.customer_details.address.line2 && (
                        <>
                          {calculation.customer_details.address.line2}<br />
                        </>
                      )}
                    </>
                  )}
                  {calculation.customer_details?.address?.city && calculation.customer_details?.address?.state && (
                    <>
                      {calculation.customer_details.address.city}, {calculation.customer_details.address.state} {calculation.customer_details.address.postal_code}<br />
                    </>
                  )}
                  {calculation.customer_details?.address?.country && (
                    <>
                      {calculation.customer_details.address.country}
                    </>
                  )}
                </Typography>
              </Box>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <PaymentsIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Currency
                </Typography>
                <Typography variant="body2">
                  {calculation.currency.toUpperCase()}
                </Typography>
              </Box>
              
              {calculation.expires_at && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <CalendarIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Expires At
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(calculation.expires_at)}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
          
          {/* Line Items */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Line Items
              </Typography>
              
              {calculation.line_items?.data && Array.isArray(calculation.line_items.data) && calculation.line_items.data.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Tax</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {calculation.line_items.data.map((item: any, index: number) => {
                        // Safely extract values
                        const reference = item.reference ?? `Item ${index + 1}`;
                        const quantity = item.quantity ?? 1;
                        const amount = item.amount ?? 0;
                        const amountTax = item.amount_tax ?? 0;
                        
                        return (
                          <TableRow key={item.id || index}>
                            <TableCell>{reference}</TableCell>
                            <TableCell>{quantity}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(amount, calculation.currency)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(amountTax, calculation.currency)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No line items available.
                </Typography>
              )}
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>
                Tax Breakdown
              </Typography>
              
              {calculation.tax_breakdown && Array.isArray(calculation.tax_breakdown) && calculation.tax_breakdown.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Jurisdiction</TableCell>
                        <TableCell>Tax Type</TableCell>
                        <TableCell align="right">Rate</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {calculation.tax_breakdown.map((item: any, index: number) => {
                        // Safely extract values
                        const state = safeGet(item, 'tax_rate_details.state', '');
                        const country = safeGet(item, 'tax_rate_details.country', 'Unknown');
                        const taxType = safeGet(item, 'tax_rate_details.tax_type', 'Unknown');
                        const percentageDecimal = safeGet(item, 'tax_rate_details.percentage_decimal', '0');
                        const amount = safeGet(item, 'amount', 0);
                        
                        // Format the jurisdiction text
                        const jurisdiction = state && country ? `${state}, ${country}` : country;
                        
                        // Format the rate
                        let rate = 'N/A';
                        try {
                          if (percentageDecimal && !isNaN(parseFloat(percentageDecimal))) {
                            rate = `${parseFloat(percentageDecimal).toFixed(2)}%`;
                          }
                        } catch (e) {
                          logger.error('Error formatting percentage:', e);
                        }
                        
                        return (
                          <TableRow key={index}>
                            <TableCell>{jurisdiction}</TableCell>
                            <TableCell>{taxType}</TableCell>
                            <TableCell align="right">{rate}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(amount, calculation.currency)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No tax breakdown available.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
        
        {safeGet(calculation, 'tax_breakdown', []) && Array.isArray(calculation.tax_breakdown) && calculation.tax_breakdown.length > 0 && (
          <Accordion sx={{ mt: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Technical Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <pre style={{ overflow: 'auto', maxHeight: '300px' }}>
                {JSON.stringify(calculation, null, 2)}
              </pre>
            </AccordionDetails>
          </Accordion>
        )}
      </Paper>
    </Box>
  );
};

export default TaxCalculationDetail; 